// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import OrderConfirmationInfoItem from "@eisinc/App/Components/OrderConfirmation/OrderConfirmationInfoItem";
import createAccount from "@eisinc/App/Handlers/Pages/OrderConfirmation/CreateAccountForGuestUser";
import { xIsAuthenticated } from "@eisinc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import { parseBoolean } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import Button from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
        // XNG-Changes
        isAuthenticated: xIsAuthenticated(state),
        addressFieldDataView: getAddressFieldsDataView(state).value,
        shipCodeForIncoTermsDisplay: getSettingsCollection(state).customSettings.shipCodeForIncoTermsDisplay,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
    loadAddressFields,
    // XNG-Changes:EIS-4360
    createAccount,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // XNG-Changes
    infoSection?: InjectableCss;
    shippingInfoSection?: InjectableCss;
    billingInformationSection?: InjectableCss;
    headingSection?: TypographyPresentationProps;
    companyLogoItem?: InjectableCss;
    fullWidthColumn?: InjectableCss;
    messageText?: TypographyPresentationProps;
    createGuestAccountSection?: InjectableCss;
    guestAccountTitle?: TypographyPresentationProps;
    guestAccountMessage?: TypographyPresentationProps;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
    },
    orderNumberGridItem: {
        width: 12,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    orderInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        css: css`
            padding-left: 5px;
            padding-bottom: 15px;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    pickUpWarehouse,
    enableVat,
    language,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
    addressFieldDataView,
    shipCodeForIncoTermsDisplay,
    isAuthenticated,
    // XNG-Changes:EIS-4360
    createAccount,
}: Props) => {
    // <XNG-Changes>
    const [guestAccountCreated, setGuestAccountCreated] = useState(false);
    const [showResultMessage, setShowResultMessage] = useState(false);
    const [isCreatingGuestAccount, setIsCreatingGuestAccount] = useState(false);
    // </XNG-Changes>

    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }

        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber });
        }
    }, [cartState]);

    if (!addressFieldDataView) {
        loadAddressFields();
    }

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    const order = orderState.value;

    const currentCarrierIdx = cart?.carriers?.findIndex(c => c.id === cart?.carrier?.id);

    let incoTerms;
    if (
        currentCarrierIdx === 0 &&
        shipCodeForIncoTermsDisplay?.split(",")?.includes(cart?.properties?.shipCode || "") &&
        cart?.properties?.billToCarrier === cart?.carrier?.description
    ) {
        incoTerms = billTo?.properties?.carrierAccounts;
    }

    if (!cart || !billTo || !shipTo) {
        return null;
    }

    const salesRep = cart.properties.salesPerson ? JSON.parse(cart.properties.salesPerson) : null;
    const orderedBy = cart.properties.orderedBy ? JSON.parse(cart.properties.orderedBy) : null;

    const lastFour = cart.paymentOptions?.creditCard?.cardNumber
        ? cart?.paymentOptions?.creditCard?.cardNumber.slice(-4)
        : null;
    const expDate = [cart.paymentOptions?.creditCard?.expirationMonth, cart.paymentOptions?.creditCard?.expirationYear]
        .filter(Boolean)
        .join(" / ");

    const hasShippingInfo =
        (cart.fulfillmentMethod === FulfillmentMethod.Ship &&
            [cart.carrier?.description, cart.shipVia?.description, incoTerms].some(Boolean)) ||
        cart.properties.carrierAccount ||
        cart.properties.additionalDetails ||
        cart.requestedDeliveryDate ||
        cart.requestedPickupDate ||
        cart.notes;

    const hasPaymentInfo =
        (cart.paymentMethod && cart.paymentMethod.description !== "PO" && lastFour && expDate) ||
        cart.poNumber ||
        cart.paymentMethod?.name === "CC" ||
        cart.paymentMethod?.name === "PO";

    /* XNG-Changes:EIS-4360 */
    const createAccountForGuestClickHandler = () => {
        setIsCreatingGuestAccount(true);
        createAccount({
            onSuccess: () => {
                setIsCreatingGuestAccount(false);
                setShowResultMessage(true);
                setGuestAccountCreated(true);
            },
            onError: () => {
                setIsCreatingGuestAccount(false);
                setShowResultMessage(true);
            },
        });
    };
    const guestUserCanCreateAccount = parseBoolean(cart.properties.guestUserCanCreateAccount);

    const renderGuestAccountContent = () => {
        if (showResultMessage) {
            return (
                <Typography {...styles.guestAccountMessage}>
                    {siteMessage(
                        guestAccountCreated ? "Custom_CreateGuestAccountSucceeded" : "Custom_CreateGuestAccountFailed",
                    )}
                </Typography>
            );
        }
        return (
            <>
                <Typography {...styles.guestAccountTitle}>{siteMessage("Custom_CreateGuestAccountTitle")}</Typography>
                <Typography {...styles.guestAccountMessage}>
                    {siteMessage("Custom_CreateGuestAccountMessage")}
                </Typography>
                <Button {...{ isLoading: isCreatingGuestAccount }} onClick={createAccountForGuestClickHandler}>
                    {translate("Create An Account")}
                </Button>
            </>
        );
    };

    return (
        <StyledWrapper {...styles.OrderInformationGridContainer}>
            <StyledWrapper {...styles.companyLogoItem}>
                <LazyImage
                    src={siteMessage("Company_Logo").toString()}
                    altText={translate("Company Logo")}
                    {...{ disableLazyLoad: true }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.infoSection} {...{ showBorderBottom: !guestUserCanCreateAccount }}>
                <OrderConfirmationInfoItem
                    label={translate("Order Number")}
                    value={cart.erpOrderNumber || cart.orderNumber}
                />
                <OrderConfirmationInfoItem
                    label={translate("Order Date")}
                    value={
                        order?.orderDate
                            ? getLocalizedDateTime({
                                  dateTime: new Date(order.orderDate as Date),
                                  language,
                              })
                            : ""
                    }
                />
                <OrderConfirmationInfoItem label={translate("PO Number")} value={cart.poNumber} />
                <OrderConfirmationInfoItem label={translate("Status")} value={cart.statusDisplay} />
                <OrderConfirmationInfoItem label={translate("Online Ref")} value={cart.orderNumber} />
                <OrderConfirmationInfoItem label={translate("Salesperson")} value={cart.salespersonName} />
                <OrderConfirmationInfoItem label={translate("VAT Number")} value={cart.customerVatNumber} />
            </StyledWrapper>
            {/* XNG-Changes:EIS-4360 */}
            {guestUserCanCreateAccount && (
                <StyledWrapper {...styles.createGuestAccountSection}>
                    <StyledWrapper>{renderGuestAccountContent()}</StyledWrapper>
                </StyledWrapper>
            )}

            {billTo && (
                <StyledWrapper {...styles.billingInformationSection}>
                    {billTo && (
                        <OrderConfirmationBillingInformation
                            cart={cart}
                            billTo={billTo}
                            extendedStyles={styles.orderConfirmationBillingInformation}
                            {...({ addressFields: addressFieldDataView?.billToAddressFields } as any)}
                        />
                    )}
                    {shipTo ? (
                        <OrderConfirmationShippingInformation
                            cart={cart}
                            shipTo={shipTo}
                            pickUpWarehouse={pickUpWarehouse}
                            extendedStyles={styles.orderConfirmationShippingInformation}
                            {...({ addressFields: addressFieldDataView?.shipToAddressFields } as any)}
                        />
                    ) : (
                        <OrderConfirmationShippingInformation
                            cart={cart}
                            shipTo={billTo}
                            pickUpWarehouse={pickUpWarehouse}
                            extendedStyles={styles.orderConfirmationShippingInformation}
                            {...({ addressFields: addressFieldDataView?.shipToAddressFields } as any)}
                        />
                    )}
                </StyledWrapper>
            )}

            {hasShippingInfo && (
                <StyledWrapper {...styles.shippingInfoSection}>
                    <Typography {...styles.headingSection}>
                        {cart.fulfillmentMethod === FulfillmentMethod.Ship
                            ? translate("Shipping Information")
                            : translate("Pick Up Information")}
                    </Typography>
                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <OrderConfirmationInfoItem
                            label={translate("Shipping Method")}
                            value={[
                                cart.carrier?.description,
                                currentCarrierIdx !== 2 && cart.shipVia?.description,
                                incoTerms,
                            ]
                                .filter(Boolean)
                                .join(" / ")}
                        />
                    )}
                    {cart.properties.carrierAccount && (
                        <OrderConfirmationInfoItem
                            label={translate("Carrier Account")}
                            value={cart.properties.carrierAccount}
                        />
                    )}
                    {cart.properties.additionalDetails && (
                        <OrderConfirmationInfoItem
                            label={translate("Additional Details")}
                            value={cart.properties.additionalDetails}
                        />
                    )}
                    {/* XNG-Change: only display pickup date since ship date is included in the lines */}
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.requestedPickupDate && (
                        <OrderConfirmationInfoItem
                            label={translate("Requested Pickup Date")}
                            value={<LocalizedDateTime dateTime={cart.requestedPickupDateDisplay} />}
                        />
                    )}
                    <OrderConfirmationInfoItem
                        label={
                            cart.fulfillmentMethod === FulfillmentMethod.Ship
                                ? translate("Shipping Notes")
                                : translate("Notes")
                        }
                        value={cart.notes}
                    />
                </StyledWrapper>
            )}

            {hasPaymentInfo && (
                <StyledWrapper {...styles.shippingInfoSection}>
                    <Typography {...styles.headingSection}>{translate("Payment Information")}</Typography>
                    {cart.paymentMethod && cart.paymentMethod.description !== "PO" && lastFour && expDate && (
                        <OrderConfirmationInfoItem
                            label={translate("Credit Card")}
                            value={translate(`****{0} Exp {1}`, lastFour, expDate)}
                        />
                    )}
                    {cart.paymentMethod?.name === "PO" && (
                        <OrderConfirmationInfoItem
                            label={translate("Payment Method")}
                            value={siteMessage("Custom_Paid_With_Terms")}
                        />
                    )}
                    {cart.paymentMethod?.name === "CC" && (
                        <OrderConfirmationInfoItem
                            label={translate("Payment Method")}
                            value={siteMessage("Custom_Paid_With_CreditCard")}
                        />
                    )}
                    {cart.poNumber && (
                        <OrderConfirmationInfoItem label={translate("Purchase Order #")} value={cart.poNumber} />
                    )}
                </StyledWrapper>
            )}

            {isAuthenticated && (salesRep || orderedBy) && (
                <StyledWrapper {...styles.shippingInfoSection}>
                    <Typography {...styles.headingSection}>{translate("Sales Representative")}</Typography>
                    {salesRep && (
                        <OrderConfirmationInfoItem
                            label={translate("Rep Info")}
                            value={[salesRep.Name, salesRep.Email, salesRep.Phone].filter(Boolean).join(", ")}
                        />
                    )}
                    {orderedBy && (
                        <OrderConfirmationInfoItem
                            label={translate("Ordered By")}
                            value={[orderedBy.Name, orderedBy.Email].filter(Boolean).join(", ")}
                        />
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
